import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import gsap from 'gsap';
import * as THREE from 'three';
import officialLogo from './assets/3Fold_Labs_Logo.png';

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const PALETTE = ['#f0d48c', '#c99a35', '#8fb39a', '#f7f1e4'];

function makeThread(index) {
  const depth = -1.6 + index * .46;
  const lift = (index - 3) * .48;
  const points = Array.from({ length: 7 }, (_, pointIndex) => {
    const x = -6.4 + pointIndex * 2.15;
    const phase = pointIndex * .82 + index * .74;
    return new THREE.Vector3(x, lift + Math.sin(phase) * .72, depth + Math.cos(phase * .7) * .3);
  });
  return new THREE.CatmullRomCurve3(points, false, 'catmullrom', .65);
}

const TravelingLogo = memo(function TravelingLogo({ curve, stage, questionIndex, scrollProgress }) {
  const mesh = useRef();
  const texture = useLoader(THREE.TextureLoader, officialLogo);
  const point = useMemo(() => new THREE.Vector3(), []);
  const tangent = useMemo(() => new THREE.Vector3(), []);
  const scaleTarget = useMemo(() => new THREE.Vector3(), []);
  const parentQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const targetQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const rollQuaternion = useMemo(() => new THREE.Quaternion(), []);
  const forwardAxis = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const initialPosition = useMemo(() => curve.getPointAt(.12), [curve]);

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    texture.needsUpdate = true;
  }, [texture]);

  useFrame(({ clock, camera }, delta) => {
    if (!mesh.current) return;
    const questionProgress = questionIndex / 24;
    const travelPhase = (clock.getElapsedTime() * .01 + scrollProgress * .18 + questionProgress * .06 + .68) % 1;
    const travel = .18 + travelPhase * .64;
    curve.getPointAt(travel, point);
    curve.getTangentAt(travel, tangent);
    point.z += stage === 'assessment' ? 3.0 : stage === 'results' ? 1.1 : .55;
    const targetScale = stage === 'assessment' ? .62 : stage === 'results' ? .78 : .82;
    const smoothing = 1 - Math.exp(-delta * 3.6);
    mesh.current.position.lerp(point, smoothing);
    scaleTarget.setScalar(targetScale);
    mesh.current.scale.lerp(scaleTarget, smoothing);
    mesh.current.parent.getWorldQuaternion(parentQuaternion);
    targetQuaternion.copy(parentQuaternion).invert().multiply(camera.quaternion);
    rollQuaternion.setFromAxisAngle(forwardAxis, Math.atan2(tangent.y, tangent.x) * .12);
    targetQuaternion.multiply(rollQuaternion);
    mesh.current.quaternion.slerp(targetQuaternion, smoothing * .62);
    mesh.current.material.uniforms.uOpacity.value = THREE.MathUtils.lerp(
      mesh.current.material.uniforms.uOpacity.value,
      stage === 'assessment' ? .34 : .23,
      smoothing
    );
  });

  return (
    <mesh ref={mesh} position={initialPosition} renderOrder={2}>
      <planeGeometry args={[1.32, 1.32]} />
      <shaderMaterial
        transparent
        depthWrite={false}
        depthTest={false}
        uniforms={{
          uMap: { value: texture },
          uOpacity: { value: .18 }
        }}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform sampler2D uMap;
          uniform float uOpacity;
          varying vec2 vUv;
          void main() {
            vec4 texel = texture2D(uMap, vUv);
            float lightFloor = min(texel.r, min(texel.g, texel.b));
            float logoMask = 1.0 - smoothstep(0.86, 0.975, lightFloor);
            float edgeFade = smoothstep(0.0, 0.08, vUv.x) * smoothstep(0.0, 0.08, vUv.y)
              * smoothstep(0.0, 0.08, 1.0 - vUv.x) * smoothstep(0.0, 0.08, 1.0 - vUv.y);
            gl_FragColor = vec4(texel.rgb, logoMask * edgeFade * uOpacity);
          }
        `}
      />
    </mesh>
  );
});

function FlowingThreads({ stage, questionIndex, scrollProgress }) {
  const group = useRef();
  const reveal = useRef(0);
  const threads = useMemo(() => Array.from({ length: 7 }, (_, index) => makeThread(index)), []);
  const facets = useMemo(() => Array.from({ length: 9 }, (_, index) => {
    const angle = index * GOLDEN_ANGLE;
    return {
      position: [Math.cos(angle) * (2.3 + (index % 3) * .6), Math.sin(angle) * (1.45 + (index % 2) * .5), -.8 + (index % 4) * .45],
      rotation: [Math.sin(angle) * .8, Math.cos(angle) * .9, angle],
      scale: .12 + (index % 3) * .035
    };
  }), []);

  useFrame((_, delta) => {
    if (!group.current) return;
    reveal.current = Math.min(1, reveal.current + delta * .72);
    const easedReveal = 1 - Math.pow(1 - reveal.current, 4);
    const questionProgress = questionIndex / 24;
    const targetX = stage === 'hero' ? .9 - scrollProgress * .8 : stage === 'assessment' ? -.15 : .25;
    const targetY = stage === 'hero' ? .25 + scrollProgress * .45 : stage === 'assessment' ? -.05 : .3;
    const targetZ = stage === 'hero' ? -1.2 + scrollProgress * .9 : stage === 'assessment' ? -2.15 : -1.6;
    const targetRotY = stage === 'assessment' ? -.32 + questionProgress * .64 : stage === 'results' ? .22 : -.18 + scrollProgress * .28;
    const targetRotZ = stage === 'assessment' ? -.05 + questionProgress * .1 : -.12 + scrollProgress * .18;
    const smoothing = 1 - Math.exp(-delta * 3.8);
    group.current.position.x = THREE.MathUtils.lerp(group.current.position.x, targetX, smoothing);
    group.current.position.y = THREE.MathUtils.lerp(group.current.position.y, targetY, smoothing);
    group.current.position.z = THREE.MathUtils.lerp(group.current.position.z, targetZ, smoothing);
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, targetRotY, smoothing);
    group.current.rotation.z = THREE.MathUtils.lerp(group.current.rotation.z, targetRotZ, smoothing);
    group.current.scale.setScalar(THREE.MathUtils.lerp(.6, 1, easedReveal));
  });

  return (
    <group ref={group} position={[5.5, -3.5, -5]} rotation={[.08, -.5, -.2]}>
      {threads.map((curve, index) => (
        <mesh key={index}>
          <tubeGeometry args={[curve, 96, .018 + (index % 3) * .008, 8, false]} />
          <meshPhysicalMaterial
            color={index % 3 === 0 ? '#102d1e' : index % 2 ? '#b78a2d' : '#d6b56c'}
            metalness={index % 3 === 0 ? .28 : .72}
            roughness={.28 + (index % 2) * .18}
            transparent
            opacity={.46 + (index % 3) * .1}
          />
        </mesh>
      ))}
      {facets.map((facet, index) => (
        <mesh key={`facet-${index}`} position={facet.position} rotation={facet.rotation} scale={facet.scale}>
          <icosahedronGeometry args={[1, 0]} />
          <meshPhysicalMaterial color={index % 2 ? '#b78a2d' : '#102d1e'} metalness={.6} roughness={.3} transparent opacity={.56} />
        </mesh>
      ))}
      {stage !== 'assessment' && <TravelingLogo curve={threads[5]} stage={stage} questionIndex={questionIndex} scrollProgress={scrollProgress} />}
    </group>
  );
}

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => setProgress(Math.min(1, window.scrollY / Math.max(1, window.innerHeight))));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', update);
    };
  }, []);
  return progress;
}

export function ExperienceCanvas({ stage, questionIndex }) {
  const scrollProgress = useScrollProgress();
  return (
    <div className={`experience-canvas stage-${stage}`} aria-hidden="true">
      <Canvas camera={{ position: [0, 0, 8], fov: 40 }} dpr={[1, 1.5]} gl={{ alpha: true, antialias: true }}>
        <ambientLight intensity={1.5} />
        <directionalLight position={[4, 6, 7]} intensity={3.2} color="#fff2cf" />
        <pointLight position={[-4, -2, 4]} intensity={2.4} color="#7d9c82" />
        <FlowingThreads stage={stage} questionIndex={questionIndex} scrollProgress={scrollProgress} />
      </Canvas>
    </div>
  );
}

function ResponsiveThread({ curve, activeAnswer }) {
  const line = useRef();
  const material = useRef();
  const response = useRef(0);
  const samples = useMemo(() => Array.from({ length: 97 }, (_, index) => curve.getPointAt(index / 96)), [curve]);
  const positions = useMemo(() => new Float32Array(samples.length * 3), [samples.length]);
  const answerIndex = ['A', 'B', 'C', 'D'].indexOf(activeAnswer);

  useFrame((_, delta) => {
    if (!line.current || !material.current) return;
    const smoothing = 1 - Math.exp(-delta * 8);
    response.current = THREE.MathUtils.lerp(response.current, answerIndex >= 0 ? 1 : 0, smoothing);
    const targetU = answerIndex < 0 ? .5 : [.24, .69, .31, .75][answerIndex];
    const rowDirection = answerIndex < 2 ? .38 : -.42;
    samples.forEach((base, index) => {
      const u = index / (samples.length - 1);
      const distance = (u - targetU) / .105;
      const influence = Math.exp(-distance * distance) * response.current;
      const offset = index * 3;
      positions[offset] = base.x;
      positions[offset + 1] = base.y + influence * rowDirection;
      positions[offset + 2] = base.z + influence * .52;
    });
    line.current.geometry.attributes.position.needsUpdate = true;
    material.current.opacity = THREE.MathUtils.lerp(.2, .82, response.current);
  });

  return (
    <line ref={line} renderOrder={3}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <lineBasicMaterial ref={material} color="#d6b56c" transparent opacity={.2} depthWrite={false} depthTest={false} />
    </line>
  );
}

export function AssessmentWatermark({ questionIndex, activeAnswer }) {
  const curve = useMemo(() => makeThread(5), []);
  return (
    <Canvas className="assessment-watermark" camera={{ position: [0, 0, 8], fov: 40 }} dpr={[1, 1.5]} gl={{ alpha: true, antialias: true }} aria-hidden="true">
      <group position={[0, -.72, -1.1]}>
        <mesh>
          <tubeGeometry args={[curve, 96, .012, 8, false]} />
          <meshBasicMaterial color="#b78a2d" transparent opacity={.16} depthWrite={false} />
        </mesh>
        <ResponsiveThread curve={curve} activeAnswer={activeAnswer} />
        <TravelingLogo curve={curve} stage="assessment" questionIndex={questionIndex} scrollProgress={0} />
      </group>
    </Canvas>
  );
}

function ConfettiBurst() {
  const meshes = useRef([]);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const particles = useMemo(() => Array.from({ length: 72 }, (_, index) => {
    const angle = index * GOLDEN_ANGLE;
    const spread = .52 + (index % 9) * .035;
    const speed = 2.8 + (index % 7) * .38;
    return {
      vx: Math.cos(angle) * speed * spread,
      vy: 3.8 + (index % 8) * .34,
      vz: Math.sin(angle * 1.7) * 1.8,
      spinX: .8 + (index % 5) * .7,
      spinY: 1.1 + (index % 7) * .55,
      scale: .62 + (index % 4) * .16,
      colorIndex: index % PALETTE.length,
      poolIndex: Math.floor(index / PALETTE.length)
    };
  }), []);

  useFrame(({ clock }) => {
    if (meshes.current.length !== PALETTE.length || meshes.current.some(mesh => !mesh)) return;
    const time = Math.min(clock.getElapsedTime(), 2.65);
    const fade = THREE.MathUtils.clamp((2.65 - time) / .65, 0, 1);
    particles.forEach((particle, index) => {
      const delay = (index % 9) * .012;
      const t = Math.max(0, time - delay);
      dummy.position.set(particle.vx * t, -2.1 + particle.vy * t - 2.2 * t * t, particle.vz * t);
      dummy.rotation.set(particle.spinX * t, particle.spinY * t, index * GOLDEN_ANGLE);
      dummy.scale.set(.075 * particle.scale * fade, .25 * particle.scale * fade, .028 * particle.scale * fade);
      dummy.updateMatrix();
      meshes.current[particle.colorIndex].setMatrixAt(particle.poolIndex, dummy.matrix);
    });
    meshes.current.forEach(mesh => { mesh.instanceMatrix.needsUpdate = true; });
  });

  return (
    <group>
      {PALETTE.map((color, colorIndex) => (
        <instancedMesh key={color} ref={node => { meshes.current[colorIndex] = node; }} args={[null, null, particles.length / PALETTE.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </instancedMesh>
      ))}
    </group>
  );
}

export function CompletionCelebration({ completed }) {
  const root = useRef();
  useLayoutEffect(() => {
    const context = gsap.context(() => {
      gsap.set('.completion-count, .completion-title span, .completion-copy', { y: 72, autoAlpha: 0 });
      gsap.set('.completion-mark', { scale: 0, rotation: -24, autoAlpha: 0 });
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .to('.completion-mark', { scale: 1, rotation: 0, autoAlpha: 1, duration: .48 })
        .to('.completion-count', { y: 0, autoAlpha: 1, duration: .3 }, '-=.2')
        .to('.completion-title span', { y: 0, autoAlpha: 1, duration: .38, stagger: .07 }, '-=.12')
        .to('.completion-copy', { y: 0, autoAlpha: 1, duration: .32 }, '-=.1');
    }, root);
    return () => context.revert();
  }, []);

  return (
    <section className="completion-celebration" ref={root} aria-live="polite" aria-label="Workstyle complete">
      <Canvas className="completion-webgl" camera={{ position: [0, 0, 8], fov: 42 }} dpr={[1, 1.5]}>
        <ambientLight intensity={1.7} />
        <directionalLight position={[3, 7, 6]} intensity={4} color="#fff0c8" />
        <ConfettiBurst />
      </Canvas>
      <div className="completion-lockup">
        <div className="completion-mark">✓</div>
        <p className="completion-count">{String(completed).padStart(2, '0')} / 25 RESPONSES CAPTURED</p>
        <h2 className="completion-title"><span>WORKSTYLE</span><span>BUILT.</span></h2>
        <p className="completion-copy">ASSEMBLING YOUR PORTABLE AI WORKSTYLE</p>
      </div>
    </section>
  );
}
